/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { HTMLMessage } from "components/page-components/dialogs/html-message";
import { CloseButton } from "components/page-components/dialogs/close-button";

const ConfirmDialog = (props) => {

    const open = props?.open;
    const onCancel = props?.onCancel;
    const secondaryAction = props?.secondaryAction;
    const primaryAction = props?.primaryAction;
    const primaryButtonLabel = props?.primaryButtonLabel;
    const secondaryButtonLabel = props?.secondaryButtonLabel;
    const showSecondaryButton = props?.showSecondaryButton;
    const title = props?.title;
    const remarks = props?.remarks;
    const message = props?.message;
    const isPrimaryDanger = props?.isPrimaryDanger;
    const isSecondaryDanger = props?.isSecondaryDanger;
    const isPrimaryDisabled = props?.isPrimaryDisabled;
    const isSecondaryDisabled = props?.isSecondaryDisabled;
    const primaryIcon = props?.primaryIcon;
    const children = props?.children;
    
    const { t } = useTranslation();
    const primaryColour = isPrimaryDanger ? "danger" : "action";
    const secondaryColour = isSecondaryDanger ? "danger" : "neutral";
    const buttonSize = "small";
    const defaultPrimaryText = t("commons.confirm");
    const defaultSecondaryText = t("commons.cancel");
    const primaryText = primaryButtonLabel || defaultPrimaryText;
    const secondaryText = secondaryButtonLabel || defaultSecondaryText;
    const shouldShowSecondaryButton = showSecondaryButton && typeof secondaryAction === "function";

    const primaryHandler = (event) => {
        primaryAction(event);
    };
    
    const secondaryHandler = (event) => {
        if (typeof secondaryAction === "function" ) {
            secondaryAction(event);
        } else if (typeof onCancel === "function" ) {
            onCancel(event);
        }
    };
    
    const closeHandler = (event) => {
        onCancel(event);
    };
    
    return (
        <Dialog
            open={open}
            onClose={onCancel}
            aria-labelledby='confirm-dialog-title'
            aria-describedby='confirm-dialog-description'
        >
            <DialogTitle>
                <span className='confirm-dialog-title'>
                    {title}
                </span>
                <CloseButton
                    closeHandler={closeHandler}
                />
                {remarks &&
                    <div className='confirm-dialog-subtitle'>
                        {remarks}
                    </div>
                }
            </DialogTitle>
            <DialogContent>
                <DialogContentText id='confirm-dialog-description'>
                    <HTMLMessage
                        message={message}
                    />
                    {children}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <div className="confirm-dialog-actions">
                    {shouldShowSecondaryButton &&
                        <Button
                            disabled={isSecondaryDisabled}
                            variant={"text"}
                            color={secondaryColour}
                            onClick={secondaryHandler}
                            size={buttonSize}
                        >
                            {secondaryText}
                        </Button>
                    }
                    <Button
                        disabled={isPrimaryDisabled}
                        color={primaryColour}
                        variant={"contained"}
                        onClick={primaryHandler}
                        size={buttonSize}
                        startIcon={primaryIcon}
                    >
                        {primaryText}
                    </Button>
                </div>
            </DialogActions>
        </Dialog>
    );
};

export { ConfirmDialog };
